import './PersonalInformationWidget.scss';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_Card } from '../../../../../design/card/card/S_Cards';
import { ValidatedTextInput } from '../../../../components/ValidatedTextInput';
import { InputStatus } from '../../../../models/error.model';
import { CountryRowContainer } from '../../containers/personalInformations/rows/CountryRow.container';
import { ChangeEmailModal } from '../ChangeEmailModal';
import { DeleteAccountModal } from '../DeleteAccountModal';
import { ResetPasswordModal } from '../ResetPasswordModal';

interface IPersonalInformationWidgetProps {
  fullName: string;
  company: string;
  email: string;
  showPasswordModal: boolean;
  passwordFormIsValid: boolean;
  oldPasswordStatus: InputStatus;
  onFullNameChange: (value: string) => void;
  onCompanyChange: (value: string) => void;
  onFullNameBlur: (value: string) => Promise<void>;
  onCompanyBlur: (value: string) => Promise<void>;
  onShowChangePasswordModal: () => void;
  onHideChangePasswordModal: () => void;
  onShowChangeEmailModal: () => void;
  onHideChangeEmailModal: () => void;
  onOldPasswordChange: (password: string) => void;
  onNewPasswordChange: (password: string) => void;
  onOldPasswordBlur: () => void;
  onNewPasswordStatusChange: (inputStatus: InputStatus) => void;
  onResetPassword: () => Promise<void>;
  showDeleteAccountModal: boolean;
  onShowDeleteAccountModal: () => void;
  onHideDeleteAccountModal: () => void;
  onDeleteAccount: () => Promise<void>;
  showChangeEmailModal: boolean;
  onPasswordChange: (password: string) => void;
  onPasswordBlur: () => void;
  passwordStatus: InputStatus;
  emailStatus: InputStatus;
  onEmailChange: (email: string) => void;
  onEmailBlur: () => void;
  onChangeEmailConfirmation: () => Promise<void>;
  emailChangePending: boolean;
}

export const PersonalInformationWidget = observer((props: IPersonalInformationWidgetProps): ReactElement => {
  const { t } = useTranslator();

  return (
    <S_Card className='personal-information-widget card-with-list'>
      <h5>{t('personal_information')}</h5>
      <div className='list-row'>
        <div className='left'>{t('full_name')}</div>
        <div className='right'>
          <ValidatedTextInput
            id='account-full-name'
            type={'text'}
            maxLength={255}
            onBlur={(val) => {
              void props.onFullNameBlur(val);
            }}
            onChange={props.onFullNameChange}
            className={'text-right'}
            placeholder={t('full_name')}
            value={props.fullName || ''}
          />
        </div>
      </div>
      <div className='list-row'>
        <div className='left'>{t('organization')}</div>
        <div className='right'>
          <ValidatedTextInput
            id='account-company'
            type={'text'}
            maxLength={255}
            onBlur={(val) => {
              void props.onCompanyBlur(val);
            }}
            onChange={props.onCompanyChange}
            className={'text-right'}
            placeholder={t('organization')}
            value={props.company || ''}
          />
        </div>
      </div>
      <CountryRowContainer />
      <div className='list-row'>
        <div className='left'>{t('email_address')}</div>
        <div className='account-email right'>
          {props.email} <i>{props.emailChangePending && t('email_change_pending')}</i>
          <Button
            variant='outlined'
            onClick={props.onShowChangeEmailModal}
          >
            {t('change_email')}
          </Button>
          <ChangeEmailModal
            show={props.showChangeEmailModal}
            passwordStatus={props.passwordStatus}
            emailStatus={props.emailStatus}
            onConfirm={props.onChangeEmailConfirmation}
            onCancel={props.onHideChangeEmailModal}
            onPasswordChange={props.onPasswordChange}
            onPasswordBlur={props.onPasswordBlur}
            onEmailChange={props.onEmailChange}
            onEmailBlur={props.onEmailBlur}
          />
        </div>
      </div>
      <div className='list-row'>
        <div className='left'>{t('password')}</div>
        <div className='right'>
          <Button
            variant='outlined'
            onClick={props.onShowChangePasswordModal}
          >
            {t('change_password')}
          </Button>
          <ResetPasswordModal
            showPasswordModal={props.showPasswordModal}
            onNewPasswordChange={props.onNewPasswordChange}
            onOldPasswordChange={props.onOldPasswordChange}
            onOldPasswordBlur={props.onOldPasswordBlur}
            onNewPasswordStatusChange={props.onNewPasswordStatusChange}
            onCancel={props.onHideChangePasswordModal}
            oldPasswordStatus={props.oldPasswordStatus}
            passwordFormIsValid={props.passwordFormIsValid}
            onConfirm={props.onResetPassword}
          />
        </div>
      </div>
      <div className='list-row'>
        <div className='left' />
        <div
          id='delete-account'
          className='right'
        >
          <Button
            id='delete-account'
            variant='outlined'
            onClick={props.onShowDeleteAccountModal}
          >
            {t('delete_account')}
          </Button>
          <DeleteAccountModal
            showDeleteAccountModal={props.showDeleteAccountModal}
            onCancel={props.onHideDeleteAccountModal}
            onConfirm={props.onDeleteAccount}
          />
        </div>
      </div>
    </S_Card>
  );
});
